/**
 * Collection Grid Swatches AJAX Handler
 * Prevents page redirects and updates product content via AJAX
 * Works with sibling swatches in product grid items
 */

class CollectionSwatchesAjax {
  constructor() {
    this.init();
  }

  init() {
    // Check if we're in a Shopify environment
    if (!window.theme || !window.theme.routes) {
      console.warn('CollectionSwatchesAjax: Shopify theme object not found');
      return;
    }
    
    this.bindEvents();
  }

  bindEvents() {
    // Use event delegation to catch clicks on sibling swatches
    document.addEventListener('click', (event) => {
      // Check if click is on a sibling swatch link in product grid
      const swatchElement = event.target.closest('[data-sibling-link]');
      
      if (swatchElement) {
        // STEP 1: Stop the redirect
        event.preventDefault();
        event.stopPropagation();
        
        // Get product URL and extract handle
        const productUrl = swatchElement.getAttribute('data-sibling-link');
        const productHandle = this.extractHandleFromUrl(productUrl);
        
        if (productHandle) {
          // Find the parent product grid item
          const gridItem = swatchElement.closest('[data-grid-item]');
          if (gridItem) {
            this.fetchProductData(productHandle, swatchElement, gridItem);
          }
        }
        
        return false;
      }
    }, true); // Use capture phase to ensure we catch it first
  }

  // Extract product handle from URL
  extractHandleFromUrl(url) {
    if (!url) return null;
    
    // Remove domain and get path
    const path = url.replace(/^https?:\/\/[^\/]+/, '');
    
    // Extract handle from /products/handle or /collections/collection/products/handle
    const matches = path.match(/\/products\/([^\/\?]+)/);
    return matches ? matches[1] : null;
  }

  // STEP 2: Fetch the data in JSON
  async fetchProductData(productHandle, swatchElement, gridItem) {
    if (!productHandle) {
      console.error('CollectionSwatchesAjax: No product handle provided');
      return;
    }
    
    // Show loading state
    this.showLoadingState(swatchElement);
    
    try {
      // Fetch product JSON data from Shopify
      const jsonUrl = `${window.theme.routes.root}products/${productHandle}.js`;
      const response = await fetch(jsonUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const productData = await response.json();
      
      // STEP 3: Update content in the grid item
      this.updateGridItemContent(productData, gridItem);
      this.updateActiveSwatch(swatchElement);
      
    } catch (error) {
      console.error('CollectionSwatchesAjax: Error fetching product data:', error);
      this.showErrorState(swatchElement);
    } finally {
      this.hideLoadingState(swatchElement);
    }
  }

  // STEP 3: Update grid item content with new product data
  updateGridItemContent(productData, gridItem) {
    // Update product title
    const titleElement = gridItem.querySelector('.product-item__title');
    if (titleElement) {
      titleElement.textContent = productData.title;
    }
    
    // Update product price
    const priceElement = gridItem.querySelector('[data-product-price]');
    if (priceElement) {
      priceElement.innerHTML = this.createPriceHTML(productData);
    }
    
    // Update product link URLs
    const productLinks = gridItem.querySelectorAll('[data-product-link]');
    productLinks.forEach(link => {
      const newUrl = `${window.theme.routes.root}products/${productData.handle}`;
      link.href = newUrl;
      link.setAttribute('data-product-link', newUrl);
    });
    
    // Update product image if available
    this.updateProductImage(productData, gridItem);
    
    // Update quick add form if present
    this.updateQuickAddForm(productData, gridItem);
    
    // Trigger theme-specific events
    this.triggerThemeEvents(gridItem);
  }

  // Update product image
  updateProductImage(productData, gridItem) {
    const imageContainer = gridItem.querySelector('[data-product-image-default]');
    if (imageContainer && productData.featured_image) {
      const imgElement = imageContainer.querySelector('img');
      if (imgElement) {
        // Update image src with new product image
        const newImageUrl = productData.featured_image.replace(/\.(jpg|jpeg|png|gif|webp)/, '_400x400.$1');
        imgElement.src = newImageUrl;
        imgElement.alt = productData.title;
        
        // Add smooth transition
        imgElement.style.transition = 'opacity 0.3s ease';
        imgElement.style.opacity = '0.5';
        
        imgElement.onload = () => {
          imgElement.style.opacity = '1';
        };
      }
    }
  }

  // Update quick add form with new variant ID
  updateQuickAddForm(productData, gridItem) {
    const forms = gridItem.querySelectorAll('form[action="/cart/add"]');
    forms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && productData.variants && productData.variants.length > 0) {
        // Use first available variant
        const firstVariant = productData.variants.find(v => v.available) || productData.variants[0];
        variantInput.value = firstVariant.id;
      }
    });
    
    // Update quick add modal handle if present
    const quickAddButtons = gridItem.querySelectorAll('[data-quick-add-modal-handle]');
    quickAddButtons.forEach(button => {
      button.setAttribute('data-quick-add-modal-handle', productData.handle);
    });
  }

  // Helper function to create price HTML from product JSON
  createPriceHTML(productData) {
    const price = productData.price ? (productData.price / 100).toFixed(2) : '0.00';
    const comparePrice = productData.compare_at_price ? (productData.compare_at_price / 100).toFixed(2) : null;
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
    
    let priceHTML = `<span class="price">${moneyFormat.replace('{{amount}}', price)}</span>`;
    
    if (comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
      priceHTML += ` <span class="price price--compare">${moneyFormat.replace('{{amount}}', comparePrice)}</span>`;
    }
    
    return priceHTML;
  }

  // Update active swatch styling
  updateActiveSwatch(clickedSwatchElement) {
    // Remove current class from all swatches in the same product
    const swatchContainer = clickedSwatchElement.closest('.product-item__siblings');
    if (swatchContainer) {
      const allSwatches = swatchContainer.querySelectorAll('.sibling__link');
      allSwatches.forEach(swatch => {
        swatch.classList.remove('sibling__link--current');
      });

      // Add current class to clicked swatch
      const clickedSwatch = clickedSwatchElement.querySelector('.sibling__link');
      if (clickedSwatch) {
        clickedSwatch.classList.add('sibling__link--current');
      }
    }
  }

  // Loading state management
  showLoadingState(swatchElement) {
    if (swatchElement) {
      swatchElement.style.opacity = '0.6';
      swatchElement.style.pointerEvents = 'none';
    }
  }

  hideLoadingState(swatchElement) {
    if (swatchElement) {
      swatchElement.style.opacity = '';
      swatchElement.style.pointerEvents = '';
    }
  }

  showErrorState(swatchElement) {
    if (swatchElement) {
      swatchElement.style.border = '2px solid #e74c3c';
      setTimeout(() => {
        swatchElement.style.border = '';
      }, 3000);
    }
  }

  // Trigger theme-specific events after content update
  triggerThemeEvents(container) {
    // Dispatch custom event for other theme components
    container.dispatchEvent(new CustomEvent('product-content-updated', {
      bubbles: true,
      detail: { container }
    }));
  }


}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionSwatchesAjax();
  });
} else {
  // DOM is already loaded
  new CollectionSwatchesAjax();
}
